import { FormEvent, useState } from "react";

import { SocialSignUpType } from "../../../api/types/social.type";
import { getUser } from "../../../api/users/getMe";
import { loginUser } from "../../../api/users/login";
import { translationsContent } from "../../../localization/translations";
import { useLanguage } from "../../../store/lang.context";
import { useLoader } from "../../../store/loader.context";
import { useUser } from "../../../store/user.context";
import { translate } from "../../../utils/translate";
import { loginSchema } from "../../../utils/validations";
import FacebookButton from "../../ui/button/FacebookSigninButton";
import GoogleButton from "../../ui/button/GoogleSigninButton";
import Button from "../../ui/button/SimpleButton";
import Input from "../../ui/input/SimpleInput/index";
import DynamicModal from "../../ui/modal/DynamicModal";
import EmailErrorPopup from "../errorPopup";
import PasswordReset from "../PasswordReset";
import classes from "./login.module.scss";

interface Props {
  setLoginIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSignUpIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSocialSignUp: React.Dispatch<React.SetStateAction<SocialSignUpType>>;
}

export type LoginModelType = {
  username: string;
  password: string;
};

const loginObject = {
  username: { value: "", isValid: true },
  password: { value: "", isValid: true },
};

const Login = ({ setLoginIsOpen, setSignUpIsOpen, setSocialSignUp }: Props) => {
  const { setUser } = useUser();
  const { setLoader } = useLoader();
  const [formData, setFormData] = useState(loginObject);
  const [passwordResetIsOpen, setPasswordResetIsOpen] = useState(false);
  const [error, setError] = useState(false);

  const [socialLoginError, setSocialLoginError] = useState(false);

  const { language } = useLanguage();

  const handleInputChange = (
    identifier: keyof LoginModelType,
    value: string | boolean
  ) => {
    setFormData((FormDataPreviousState) => ({
      ...FormDataPreviousState,
      [identifier]: {
        value,
        isValid: !loginSchema.validate({ [identifier]: value }).error,
      },
    }));
  };

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setLoader(true);
    const input = {
      username: formData.username.value,
      password: formData.password.value,
    };

    const validate = loginSchema.validate(input);

    if (validate.error) {
      setError(true);
      for (const key in formData) {
        const validationItem = formData[key as keyof LoginModelType];
        const tmpValidate = loginSchema.validate({
          [key]: validationItem.value,
        });
        if (tmpValidate.error)
          setFormData((prevState) => ({
            ...prevState,
            [key]: {
              value: validationItem.value,
              isValid: false,
            },
          }));
      }
      setLoader(false);
      return;
    }
    setError(false);

    const isLogged = await loginUser(input);
    if (isLogged) {
      const { success, data } = await getUser();
      if (success) {
        setUser(data);
        setLoginIsOpen(false);
      } else {
        setError(true);
      }
    } else {
      setError(true);
    }
    setLoader(false);
  };

  const resetPassword = () => {
    setPasswordResetIsOpen(true);
  };

  const close = () => {
    setSocialLoginError(false);
  };

  return (
    <>
      <DynamicModal onClose={() => setLoginIsOpen(false)}>
        <div className={classes.container}>
          <h2 className={classes.heading}>
            {translate(translationsContent, "loginHeader1", language)}
          </h2>
          <form
            className={classes.inputs}
            id="loginForm"
            onSubmit={(event) => {
              login(event);
            }}
          >
            <Input<LoginModelType, "username">
              isValid={formData.username.isValid}
              error={error}
              name="email"
              type="email"
              placeholder={`${translate(
                translationsContent,
                "setEmail",
                language
              )}`}
              required
              identifier="username"
              onChange={handleInputChange}
              autoComplete={true}
            />
            <Input<LoginModelType, "password">
              isValid={formData.password.isValid}
              error={error}
              name="password"
              type="password"
              identifier="password"
              placeholder={`${translate(
                translationsContent,
                "setPassword",
                language
              )}`}
              required
              onChange={handleInputChange}
              autoComplete={true}
            />

            <div className={classes.forgotPassword}>
              <p
                className={classes.forgotPasswordButton}
                onClick={resetPassword}
              >
                Mot de passe oublié ?
              </p>
            </div>
            {passwordResetIsOpen && (
              <PasswordReset setPasswordResetIsOpen={setPasswordResetIsOpen} />
            )}

            {error && (
              <p style={{ color: "red" }}>
                {translate(translationsContent, "loginError", language)}
              </p>
            )}
            <div className={classes.button}>
              <Button
                label={`${translate(
                  translationsContent,
                  "loginHeader",
                  language
                )}`}
                disabled={
                  formData.username.value === "" ||
                  formData.password.value === ""
                }
              />
            </div>
          </form>
          <div className={classes.dividerContainer}>
            <span className={classes.divider2} />
            <p style={{ color: "#555", fontSize: "18px" }}>
              {translate(translationsContent, "loginHeader4", language)}
            </p>
            <span className={classes.divider2} />
          </div>
          <div className={classes.dividerContainer}>
            <p>{translate(translationsContent, "loginHeader3", language)}</p>
          </div>
          <div className={classes.buttonGroup}>
            <GoogleButton
              setSocialSignUp={setSocialSignUp}
              setLoginIsOpen={setLoginIsOpen}
              setSignUpIsOpen={setSignUpIsOpen}
              setError={setSocialLoginError}
            />
            <FacebookButton
              setSocialSignUp={setSocialSignUp}
              setLoginIsOpen={setLoginIsOpen}
              setSignUpIsOpen={setSignUpIsOpen}
              setError={setSocialLoginError}
            />
          </div>
          <div className={classes.dividerContainer}>
            <span className={classes.divider} />
            <p className="nowrap">
              {translate(translationsContent, "loginHeader2", language)}
            </p>
            <span className={classes.divider} />
          </div>
          <div className={classes.button}>
            <Button
              label={`${translate(
                translationsContent,
                "loginSignupBtn",
                language
              )}`}
              onClick={() => {
                setSignUpIsOpen(true);
                setLoginIsOpen(false);
              }}
            />
          </div>
        </div>
      </DynamicModal>
      {socialLoginError && <EmailErrorPopup onClose={close} />}
    </>
  );
};

export default Login;
